import React, { useCallback } from 'react';
import { useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { Chip, Table, TableBody, TableCell, TableRow, Tooltip, Typography } from '@material-ui/core';
import { useStyles } from '../DiagnosticRevisionTable.style';
import { tableHeadColumns } from '../constants/constants';
import { diagnosticsClassColor } from '../../../../../../helpers/manageColors';
import DiagnosticsTableRow from './DiagnosticsTableRow';
import TableHeader from './TableHeader';

const SectionPerBreast = ({ section, shared }) => {
  const {
    title,
    findingsPerBreast,
    projections,
    hasActiveFilters,
    findingsLength,
    highestClass,
    empty,
    noResults,
    tableBodyRef,
    storedHeight,
    tableSide,
    orderBy,
    order,
    onRequestSort,
  } = section;
  const {
    zoomedProjection,
    hoveredClass,
    hoveredProjections,
    exam_id,
    typeValue,
    classValue,
    onClearHover,
    onClearSingleBoxHover,
    rowProps,
  } = shared;
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const breastZoomed = zoomedProjection
    ? zoomedProjection === 'lcc' || zoomedProjection === 'lmlo'
      ? 'left'
      : 'right'
    : null;
  const displayWhenZoomed = tableSide === breastZoomed ? 'block' : 'none';

  const handleClearHover = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      onClearHover();
    },
    [onClearHover]
  );

  const handleSetHover = useCallback(() => {
    onClearHover(highestClass, projections);
    onClearSingleBoxHover();
  }, [onClearHover, onClearSingleBoxHover, highestClass, projections]);

  const discardAllMouseEvents = useCallback(() => {
    if (hoveredClass) {
      onClearHover();
    }
    onClearSingleBoxHover();
  }, [hoveredClass, onClearHover, onClearSingleBoxHover]);

  return (
    <div
      className={classes.section_per_breast}
      onMouseLeave={discardAllMouseEvents}
      style={{ display: zoomedProjection ? displayWhenZoomed : 'inherit' }}>
      <div className={classes.section_header}>
        <Typography variant='h6' className={classes.section_title}>
          {zoomedProjection ? t(`Dashboard.${zoomedProjection}`) : title}
        </Typography>

        {/* Highest Class  */}
        {highestClass && (
          <div className={classes.highest_class_badge_container}>
            <Typography
              style={{
                color: '#a3a3a3',
                fontSize: theme.fonts.responsiveMediumSmall,
                letterSpacing: '0.05rem',
              }}>
              {t(`diagnose.high_class`)}
            </Typography>

            <Tooltip
              title={
                <Typography className={classes.tooltip_text}>
                  {t(`diagnose.highest_class`, { title, class: t(`revision.${highestClass}`) })}
                </Typography>
              }
              arrow
              placement='top-end'>
              <Chip
                size='small'
                variant='default'
                onMouseEnter={handleSetHover}
                onMouseOut={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    handleClearHover(e);
                  }
                }}
                onMouseDown={handleClearHover}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                className={classes.highest_class_badge}
                style={{
                  background: `${diagnosticsClassColor(highestClass, theme)}80`,
                }}
                label={t(`revision.${highestClass}`)}
              />
            </Tooltip>
          </div>
        )}
      </div>

      <Table className={classes.table}>
        <TableHeader
          tableHead={tableHeadColumns(t)}
          classes={classes}
          empty={findingsPerBreast?.length === 0 || empty}
          orderBy={orderBy}
          order={order}
          onRequestSort={onRequestSort}
        />
        <TableBody ref={tableBodyRef} className={classes.table_body}>
          {findingsPerBreast?.length > 0 ? (
            findingsPerBreast?.map((box, index) => (
              <DiagnosticsTableRow
                key={box?.clustered || box?.isMatchedMainRow ? box?.object_id : box?.finding_id}
                lesionAllInfo={box}
                index={index}
                nested={false}
                {...rowProps}
              />
            ))
          ) : (
            <TableRow
              style={{
                height:
                  hoveredClass && storedHeight ? `${storedHeight}px` : `calc(70px * ${findingsLength || 1})`,
              }}>
              <TableCell colSpan={7} className={classes.table_placeholder} align='center'>
                {empty ? (
                  <div align='center'>
                    {exam_id &&
                      (noResults ? (
                        <div className={classes.empty_chip_container}>
                          <Typography className={classes.diagnostic_birads_label}>
                            {t('revision.birads1')}
                          </Typography>
                        </div>
                      ) : (
                        <Typography className={classes.table_placeholder}>
                          {t('ResultView.is_not_evaluated')}
                        </Typography>
                      ))}
                  </div>
                ) : hoveredClass ? (
                  t(`revision.hovered_class`, {
                    class: t(`revision.${hoveredClass}`),
                    breast: t(`diagnose.${hoveredProjections?.[0]}_breast`),
                  })
                ) : hasActiveFilters ? (
                  t('revision.no_findings_with_filters', {
                    class: t(`revision.${classValue}`),
                    type: t(`revision.${typeValue}`),
                  })
                ) : (
                  t('revision.birads1')
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default SectionPerBreast;
